import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../store/authStore";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:5001";

/**
 * Open a task or project list item from anywhere in the app (My Day,
 * notifications): switches to its organization and project first.
 * Items look like { type: "task" | "listItem", id, parentTaskId, listId,
 * board: { id }, project: { id }, organization: { id } }.
 */
export default function useOpenItem() {
  const navigate = useNavigate();

  return useCallback(
    async (item) => {
      const { token, user, setCurrentProject, fetchProjects } =
        useAuthStore.getState();

      if (
        item.organization?.id &&
        String(item.organization.id) !== String(user?.currentOrganization)
      ) {
        const response = await fetch(
          `${API_BASE}/api/auth/switch-organization`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ organizationId: item.organization.id }),
          },
        );

        if (!response.ok) {
          throw new Error("Failed to switch organization");
        }

        const { user: updatedUser, currentProject } = await response.json();
        useAuthStore.setState({ user: updatedUser, currentProject });
        await fetchProjects();
      }

      if (item.project?.id) {
        const findProject = () =>
          useAuthStore
            .getState()
            .projects.find((p) => String(p._id) === String(item.project.id));
        let fullProject = findProject();
        // Projects may not be loaded yet (e.g. right after the app started)
        if (!fullProject) {
          await fetchProjects();
          fullProject = findProject();
        }
        if (fullProject) {
          setCurrentProject(fullProject);
        }
      }

      if (item.type === "listItem") {
        const page =
          item.listScope === "personal" ? "personal-lists" : "project-lists";
        navigate(`/${page}?selectList=${item.listId}`);
      } else {
        // Subtasks are opened through their parent task
        let url = `/boards?selectTask=${item.parentTaskId || item.id}`;
        if (item.board?.id) {
          url += `&boardId=${item.board.id}`;
        }
        navigate(url);
      }
    },
    [navigate],
  );
}
